import {configureStore} from '@reduxjs/toolkit'
import {createReduxEnhancer} from '@sentry/react'
import middlewareList from 'middlewares'
// Reducers
import {reducer as book} from 'modules/book'
import {
  bookingCreationReducer,
  type BookingStatusPollingController,
  createBookingStatusPollingController
} from 'modules/bookingCreation'
import {reducer as common} from 'modules/common'
import {reducer as localization} from 'modules/localization'
import meta, {augmentMetaState} from 'modules/meta'
import {reducer as overlay} from 'modules/overlay'
import psd2Auth from 'modules/psd2Auth/slice'
import {AnyAction, combineReducers, Store} from 'redux'
import {removePersonalData} from 'store/removePersonalData'

import {
  removeUserData,
  removeUserProfile
} from '@vio/core/src/auth/modules/middleware'
import {State as MetaState} from '@vio/core/src/auth/modules/selectors'
import authentication from '@vio/core/src/auth/modules/slice'
import {bookApi} from '@vio/core/src/booking/services/bookApi'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {otpApi} from '@vio/core/src/booking/services/otpApi'
import experiments from '@vio/core/src/experiments/modules/slice'
import {localizationApi} from '@vio/core/src/localization/business/getCountriesList'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {offerConfigurationApi} from '@vio/core/src/offer/services/offerConfigurationApi'
import {paymentSettingsApi} from '@vio/core/src/offer/services/paymentSettingsApi'
import {getSapiClient} from '@vio/core/src/sapi/services'
import {
  searchApi,
  SearchApiThunkExtra
} from '@vio/core/src/sapi/services/searchApi'
import {trustpilotApi} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {noop} from '@vio/core/src/utils/function'
import emailSignUp from '@vio/shared/src/authentication/Email/slice'
import dealFreeze from '@vio/shared/src/dealFreeze/modules/slice'
import {dealFreezeApi} from '@vio/shared/src/dealFreeze/services/dealFreezeApi'
import {priceWatchApi} from '@vio/shared/src/priceWatch/services'
import priceWatch from '@vio/shared/src/priceWatch/slice'
import {notificationPreferencesApi} from '@vio/shared/src/user/notificationPreferences/modules'

import type {Toggle} from 'types/Toggle'

const createReducer = () =>
  combineReducers({
    meta,
    book,
    bookingCreation: bookingCreationReducer,
    localization,
    overlay,
    common,
    authentication,
    dealFreeze,
    psd2Auth,
    experiments,
    emailSignUp,
    priceWatch,
    [bookApi.reducerPath]: bookApi.reducer,
    [priceWatchApi.reducerPath]: priceWatchApi.reducer,
    [searchApi.reducerPath]: searchApi.reducer,
    [offerCheckApi.reducerPath]: offerCheckApi.reducer,
    [dealFreezeApi.reducerPath]: dealFreezeApi.reducer,
    [localizationApi.reducerPath]: localizationApi.reducer,
    [offerConfigurationApi.reducerPath]: offerConfigurationApi.reducer,
    [bookingRetrieveApi.reducerPath]: bookingRetrieveApi.reducer,
    [otpApi.reducerPath]: otpApi.reducer,
    [notificationPreferencesApi.reducerPath]:
      notificationPreferencesApi.reducer,
    [paymentSettingsApi.reducerPath]: paymentSettingsApi.reducer,
    [trustpilotApi.reducerPath]: trustpilotApi.reducer
  })

const sentryReduxEnhancer = createReduxEnhancer({
  attachReduxState: false,
  actionTransformer: action =>
    removePersonalData(removeUserData(action) as AnyAction),
  stateTransformer: state => removeUserProfile(state as MetaState)
})

export interface CheckoutThunkExtra extends SearchApiThunkExtra {
  bookingStatusPollingController: BookingStatusPollingController
  toggle: Toggle
}

export const initializeStore = (
  initialState: Partial<RootState> = {},
  {toggle}: Pick<CheckoutThunkExtra, 'toggle'>
): Store => {
  const isServer = typeof window === 'undefined'

  return configureStore({
    reducer: createReducer(),
    preloadedState: {
      ...initialState,
      meta: isServer
        ? (initialState.meta as RootState['meta'])
        : augmentMetaState(initialState)
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        // We wanted to share RTQ API between packages and pass
        // response transform functions as action parameter
        serializableCheck: false,
        immutableCheck: false,
        thunk: {
          extraArgument: {
            getSapiClient: isServer
              ? (noop as typeof getSapiClient)
              : getSapiClient,
            bookingStatusPollingController:
              createBookingStatusPollingController(),
            toggle
          }
        }
      }).concat(...middlewareList),
    enhancers: defaultEnhancers => defaultEnhancers.concat(sentryReduxEnhancer)
  })
}

type CreateReducer = ReturnType<typeof createReducer>
export type RootState = ReturnType<CreateReducer>
